<template>
    <div class="main-panel">

        <section class="winner-section">

            <div>
                <h1 class="search-title pl-2">Select A Bonus Winner</h1>
                <div class="result-title">

                    <div class="search-content">
                        <select class="dropdown" v-model="selectedCompetitionId">
                            <option disabled :value="null">Select a competition</option>
                            <option v-for="competition in activeCompetitions"
                                    :key="competition.competitionId"
                                    :value="competition.competitionId">
                                {{ competition.competitionTitle }} (WSP{{ competition.competitionId }})
                            </option>
                        </select>

                        <button class="select-winner-button"
                                @click="selectBonusWinner"
                                :disabled="isSubmitting">
                            <span v-if="!isSubmitting">Select Bonus Winner</span>
                            <LoaderDots v-if="isSubmitting" />
                        </button>
                    </div>

                </div>
                <div class="content">

                    <div class="search-title">Prize</div>
                    <p>Enter the bonus prize name. Letters and numbers only, no spaces or special characters.</p>
                    <div class="field-grid">
                        <input id="prize"
                               type="text"
                               class="text-input"
                               v-model="prize"
                               placeholder="e.g. BonusTV" />
                    </div>

                    <div class="search-title" style="margin-top: 1.5rem;">Entry Cut-off Date &amp; Time</div>
                    <p>Only entries submitted on or before this date and time will be included in the draw.</p>
                    <div class="field-grid">
                        <input id="cutoff"
                               type="datetime-local"
                               class="text-input"
                               v-model="cutOffDateTime" />
                    </div>

                    <div class="search-title" v-if="bonusWinnerDetailsList.length" style="margin-top: 1.5rem;">Draw Results</div>

                    <div v-if="loadingWinner" class="loader">Running bonus draw...</div>

                    <div v-else class="info-grid" v-for="(winner, index) in bonusWinnerDetailsList" :key="index">

                        <label><strong>Prize:</strong></label>
                        <input disabled :value="winner.prize" readonly>

                        <label><strong>Competition Id:</strong></label>
                        <input disabled :value="winner.competitionId" readonly>

                        <label><strong>Number of Tickets Entered:</strong></label>
                        <input disabled :value="winner.numberOfTicketsEntered" readonly>

                        <label><strong>Hash:</strong></label>
                        <textarea disabled rows="5" cols="33">{{ winner.hash }}</textarea>

                        <label><strong>Kms Signature:</strong></label>
                        <textarea disabled rows="5" cols="33">{{ winner.kmsSignature }}</textarea>

                        <label><strong>Random.org Signature:</strong></label>
                        <textarea disabled rows="5" cols="33">{{ winner.randomOrgSignature }}</textarea>

                        <label><strong>Winning Ticket:</strong></label>
                        <input disabled :value="winner.winningTicket" readonly>

                        <label><strong>Winning User Id:</strong></label>
                        <input disabled :value="winner.userId" readonly>

                        <br/>
                        <a target="_blank" class="goto-winner-button" :href="`/users/${winner.userId}`">
                            see winner details
                        </a>
                    </div>

                </div>
            </div>
        </section>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import competitionService from "../services/competition-service";
import bonusWinnersService from "../services/bonus-winners-service";
import ActiveCompetitionsResponse from "../responses/ActiveCompetitionsResponse";
import BonusWinnerDetailsResponse from "../responses/BonusWinnerDetailsResponse";
import { showAlert } from "../utils/global";
import LoaderDots from "../components/LoadingButtonDots.vue";
import Swal from 'sweetalert2';

export default defineComponent({
    components: {
        LoaderDots,
    },
    setup() {
        const activeCompetitions = ref<ActiveCompetitionsResponse[]>([]);
        const selectedCompetitionId = ref<number | null>(null);
        const bonusWinnerDetailsList = ref<BonusWinnerDetailsResponse[]>([]);
        const loadingWinner = ref(false);
        const isSubmitting = ref(false);
        const prize = ref<string>('');
        const cutOffDateTime = ref<string>('');

        const MAX_LENGTH = 50;
        const ALPHANUMERIC_REGEX = /^[a-zA-Z0-9]+$/;

        const loadActiveCompetitions = async () => {
            try {
                const response = await competitionService.getActiveCompetitions();
                activeCompetitions.value = response ?? [];
            } catch (error) {
                console.error("Error fetching active competitions:", error);
            }
        };

        onMounted(async () => {
            await loadActiveCompetitions();
        });

        const validate = (): boolean => {
            if (!selectedCompetitionId.value) {
                showAlert("No competition selected", "Please select a competition.", "error");
                return false;
            }

            if (!prize.value.trim()) {
                showAlert("No prize entered", "Please enter a prize before running the draw.", "error");
                return false;
            }

            if (!ALPHANUMERIC_REGEX.test(prize.value.trim())) {
                showAlert("Invalid characters", "Only letters and numbers are allowed in the prize name.", "error");
                return false;
            }

            if (prize.value.trim().length > MAX_LENGTH) {
                showAlert("Prize too long", `Prize must be ${MAX_LENGTH} characters or fewer.`, "error");
                return false;
            }

            if (!cutOffDateTime.value) {
                showAlert("No cut-off time set", "Please select an entry cut-off date and time.", "error");
                return false;
            }

            return true;
        };

        const confirm = async (): Promise<boolean> => {
            const cutOffFormatted = new Date(cutOffDateTime.value).toLocaleString();

            const result = await Swal.fire({
                icon: 'question',
                title: 'Confirm Bonus Draw',
                html: `
                    <p><strong>Competition:</strong> WSP${selectedCompetitionId.value}</p>
                    <p><strong>Prize:</strong> ${prize.value.trim()}</p>
                    <p><strong>Cut-off:</strong> ${cutOffFormatted}</p>
                    <p style="margin-top: 1rem;">Continue?</p>
                `,
                showCancelButton: true,
                confirmButtonText: 'Yes, run draw',
                cancelButtonText: 'Cancel'
            });

            return result.isConfirmed;
        };

        const selectBonusWinner = async () => {
            if (!validate()) return;
            if (!await confirm()) return;

            loadingWinner.value = true;
            isSubmitting.value = true;

            try {
                const response = await bonusWinnersService.selectBonusWinner(selectedCompetitionId.value!, {
                    isTest: false,
                    prize: prize.value.trim(),
                    excludedTickets: [],
                    cutOffDateTime: new Date(cutOffDateTime.value).toISOString()
                });

                if (response.isSuccess) {
                    response.prize = prize.value.trim();
                    bonusWinnerDetailsList.value.push(response);
                } else {
                    showAlert(
                        "Failed to Select Bonus Winner",
                        `There was a problem selecting the bonus winner: ${response.message}`,
                        "error"
                    );
                }
            } catch (error: any) {
                const message =
                    error?.response?.data?.errors?.[0] ??
                    error?.response?.data?.message ??
                    error?.message ??
                    "An unexpected error occurred.";

                showAlert(
                    "Failed to Select Bonus Winner",
                    `An error occurred while selecting the bonus winner: ${message}`,
                    "error"
                );
            } finally {
                loadingWinner.value = false;
                isSubmitting.value = false;
            }
        };

        return {
            activeCompetitions,
            selectedCompetitionId,
            selectBonusWinner,
            bonusWinnerDetailsList,
            loadingWinner,
            isSubmitting,
            prize,
            cutOffDateTime,
        };
    },
});
</script>

<style scoped>
    strong {
        font-weight: 600;
    }

    .pl-2 {
        padding-left: 2rem;
    }

    .winner-section {
        width: calc(100% - 3rem);
        margin-inline: auto;
        display: grid;
        gap: 1rem;
        margin-top: 3rem;
    }

    .result-title {
        position: relative;
        background-color: var(--clr-primary);
        padding: 1rem 1rem;
        margin-inline: 2rem;
        color: white;
        font-size: 24px;
        border-radius: 3px;
        box-shadow: 0 10px 30px -12px rgba(0, 0, 0, 0.42), 0 4px 25px 0px rgba(0, 0, 0, 0.12), 0 8px 10px -5px rgba(0, 0, 0, 0.2);
        z-index: 1;
    }

    .search-title {
        font-size: 1.8em;
        color: #726e6e;
        margin-bottom: 10px;
    }

    label {
        padding-top: 1rem;
    }

    .dropdown {
        width: 100%;
        padding: 1rem;
        font-size: 16px;
        border: 1px solid #ccc;
        border-radius: 3px;
        background-color: white;
    }

    .search-content {
        display: grid;
        grid-template-columns: 3fr 1fr;
        gap: 2rem;
    }

    .content {
        margin: auto;
        position: relative;
        background-color: #fff;
        padding: 80px 40px 20px 40px;
        box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.14);
        border-radius: 3px;
        margin-top: -50px;
    }

    .field-grid {
        display: grid;
        padding: 0 20px 20px 20px;
    }

    .text-input {
        padding: 1rem;
        font-size: 16px;
        border: 1px solid #ccc;
        border-radius: 3px;
    }

    .select-winner-button {
        background-color: orangered;
        width: 100%;
        color: white;
        font-size: 24px;
        border-radius: 3px;
        border: none;
        cursor: pointer;
        padding: 1rem;
        height: 60px;
    }

    .goto-winner-button {
        margin: 10px 0;
        background-color: var(--clr-primary);
        padding: 1rem;
        color: white;
        font-size: 18px;
        border-radius: 3px;
        border: none;
        width: 100%;
        cursor: pointer;
        max-width: 300px;
        text-decoration: none;
        text-align: center;
    }

    .loader {
        font-weight: bold;
    }

    input, textarea {
        padding: 1rem;
    }

    .info-grid {
        display: grid;
        border: 1px solid black;
        padding: 20px;
        padding-top: 0;
        margin-bottom: 20px;
    }

    @media (max-width: 1350px) {
        .search-content {
            grid-template-columns: 1fr;
            gap: 0.5rem;
        }
    }

    @media (max-width: 450px) {
        .content {
            padding: 10px !important;
        }

        .result-title {
            margin-inline: 0.2rem;
        }
    }
</style>
